/** biome-ignore-all lint/correctness/noUnusedImports: reason */

import React, { createContext, useContext, useEffect, useState } from "react";

import { debugError, debugLog } from "../../helpers/debug";
import { useAuth } from "../auth";
import { useFirebase } from "../Firebase";
import { getObjFromLink, transformDocSnap } from "../helpers";
import { formatCompanyData } from "./formatting";

const CompanyContext = createContext({});

export const CompanyProvider = ({ children }) => {
  const firebase = useFirebase();
  const { claims } = useAuth();

  const [company, setCompany] = useState();

  useEffect(() => {
    if (!claims?.companyLink) {
      debugLog("[CompanyProvider] No companyLink, skipping company listener");
      return () => {};
    }

    debugLog("[CompanyProvider] Setting up company listener");

    return firebase.company(getObjFromLink(claims.companyLink).id).onSnapshot(
      snap => setCompany(transformDocSnap(snap)),
      err => debugLog(err)
    );
  }, [
    claims.companyLink,
    firebase,
  ]);

  const updateCompany = async company => {
    if (!claims?.companyLink) {
      debugError("[CompanyProvider] Cannot update company - no companyLink");
      return;
    }

    const updatedCompany = formatCompanyData(company);

    firebase
      .company(getObjFromLink(claims.companyLink).id)
      .update(updatedCompany);
  };

  const archiveCompany = async isArchive => {
    if (!claims?.companyLink) {
      debugError("[CompanyProvider] Cannot archive company - no companyLink");
      return;
    }

    return firebase.company(getObjFromLink(claims.companyLink).id).update({
      dateUpdated: new Date(),
      isArchive: !isArchive,
    });
  };

  const clientEmailNotificationsHandle = async value => {
    if (!claims?.companyLink) {
      debugError(
        "[CompanyProvider] Cannot update notifications - no companyLink"
      );

      return;
    }

    return firebase.company(getObjFromLink(claims.companyLink).id).update({
      dateUpdated: new Date(),
      sendEmailNotificationToClients: value,
    });
  };

  const adminEmailNotificationsHandle = async value => {
    if (!claims?.companyLink) {
      debugError(
        "[CompanyProvider] Cannot update admin notifications - no companyLink"
      );

      return;
    }

    if (value.sendEmailNotificationToAdmin) {
      return firebase.company(getObjFromLink(claims.companyLink).id).update({
        adminNotificationEmailAddress: value.email,
        dateUpdated: new Date(),
        sendEmailNotificationToAdmin: value.sendEmailNotificationToAdmin,
      });
    }

    return firebase.company(getObjFromLink(claims.companyLink).id).update({
      adminNotificationEmailAddress: firebase.firestore.FieldValue.delete(),
      dateUpdated: new Date(),
      sendEmailNotificationToAdmin: value.sendEmailNotificationToAdmin,
    });
  };
  return (
    <CompanyContext.Provider
      value={{
        adminEmailNotificationsHandle,
        archiveCompany,
        clientEmailNotificationsHandle,
        company,
        updateCompany,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const CompanyConsumer = CompanyContext.Consumer;

export const useCompany = () => useContext(CompanyContext);
